'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import Image from 'next/image';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion'
import { CheckCircle, ArrowRight, MessageSquare, Instagram, Facebook, Linkedin, Youtube, Sparkles, Clock, Shield, Eye, Trash2 } from 'lucide-react'
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PLANS, TRIAL_DAYS } from '@/lib/stripe-config';

export default function LandingPage() {
  const [isClient, setIsClient] = useState(false)
  const heroRef = useRef(null)
  const featuresRef = useRef(null)
  const howItWorksRef = useRef(null)
  const platformsRef = useRef(null)
  const pricingRef = useRef(null)
  const testimonialsRef = useRef(null)
  const ctaRef = useRef(null)

  const heroInView = useInView(heroRef, { once: false, amount: 0.3 })
  const featuresInView = useInView(featuresRef, { once: false, amount: 0.3 })
  const howItWorksInView = useInView(howItWorksRef, { once: false, amount: 0.3 })
  const platformsInView = useInView(platformsRef, { once: false, amount: 0.3 })
  const pricingInView = useInView(pricingRef, { once: true, amount: 0.3 })
  const testimonialsInView = useInView(testimonialsRef, { once: true, amount: 0.3 })
  const ctaInView = useInView(ctaRef, { once: true, amount: 0.3 })

  const { scrollYProgress } = useScroll()
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.2])

  useEffect(() => {
    setIsClient(true)
  }, [])

  const features = [
    {
      icon: <MessageSquare className="h-10 w-10 text-primary" />,
      title: "AI-Powered Responses",
      description: "Save time with intelligent comment responses generated by AI. Personalize and send with one click."
    },
    {
      icon: <Instagram className="h-10 w-10 text-primary" />,
      title: "Multi-Platform Support",
      description: "Manage comments across Instagram, Facebook, LinkedIn, and YouTube from a single dashboard."
    },
    {
      icon: <Clock className="h-10 w-10 text-primary" />,
      title: "Time-Saving Workflow",
      description: "Cut your social media management time by up to 70% with our streamlined interface."
    },
    {
      icon: <Sparkles className="h-10 w-10 text-primary" />,
      title: "Engagement Boost",
      description: "Increase your engagement rates by responding to more comments quickly and effectively."
    }
  ]

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Social Media Manager",
      company: "",
      content: "Ripli has completely transformed how I manage social media for our clients. The AI responses are incredibly natural, and I can handle 3x more accounts in the same amount of time.",
      avatar: "/Sarah.png?height=60&width=60" // Keep original src for logic, Image component handles placeholder
    },
    {
      name: "Michael Chen",
      role: "Marketing Director",
      company: "",
      content: "We've seen a 40% increase in engagement since using Ripli. The ability to quickly respond to comments across all platforms has been a game-changer for our brand.",
      avatar: "/Michael.png?height=60&width=60" // Keep original src for logic, Image component handles placeholder
    },
    {
      name: "Emma Rodriguez",
      role: "Influencer",
      company: "",
      content: "As a creator, I was struggling to keep up with all the comments. Ripli helps me stay connected with my audience without spending hours each day on responses.",
      avatar: "/Emma.png?height=60&width=60" // Keep original src for logic, Image component handles placeholder
    }
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <motion.section
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ opacity: isClient ? opacity : 1 }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 z-0" />
        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1
              className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7 }}
            >
              Manage Social Media Comments with AI-Powered Efficiency
            </motion.h1>
            <motion.p
              className="text-xl mb-8 text-muted-foreground"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              Ripli helps you respond to comments across all your social platforms with AI-generated responses, saving you hours every day.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button size="lg" className="text-lg px-8" asChild>
                <Link href="/auth/signin"><ArrowRight className="ml-2 h-5 w-5" /> Start Free Trial</Link>
              </Button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-16 relative max-w-5xl mx-auto"
          >
            <div className="bg-gradient-to-r from-primary/20 to-primary/10 rounded-xl p-1">
              <div className="bg-card rounded-lg shadow-2xl overflow-hidden">
                <Image
                  src="/ripli-dashboard.png"
                  alt="Ripli Dashboard"
                  width={1200}
                  height={600}
                  className="w-full h-auto"
                  priority
                />
              </div>
            </div>

            {/* Floating elements animation */}
            {/* Left side - platform comments */}
            <motion.div
              className="absolute hidden md:block top-4 -left-6 bg-primary/80 text-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, -8, 0],
                rotate: [0, 2, 0]
              }}
              transition={{
                repeat: Infinity,
                duration: 5,
                ease: "easeInOut"
              }}
            >
              <div className="flex items-center gap-2">
                <Instagram className="h-5 w-5" />
                <span className="font-medium">New comment</span>
              </div>
            </motion.div>

            <motion.div
              className="absolute hidden md:block top-1/4 -left-10 bg-blue-500 text-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, 6, 0],
                rotate: [0, -1, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 4.5,
                ease: "easeInOut",
                delay: 0.5,
              }}
            >
              <div className="flex items-center gap-2">
                <Facebook className="h-5 w-5" />
                <span className="font-medium">New comment</span>
              </div>
            </motion.div>

            <motion.div
              className="absolute hidden md:block top-1/2 -left-12 bg-primary/80 text-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, 10, 0],
                rotate: [0, 3, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 6,
                ease: "easeInOut",
                delay: 1.5,
              }}
            >
              <div className="flex items-center gap-2">
                <Youtube className="h-5 w-5" />
                <span className="font-medium">New comment</span>
              </div>
            </motion.div>

            <motion.div
              className="absolute hidden md:block top-3/4 -left-10 bg-blue-700 text-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, -12, 0],
                rotate: [0, -2, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 5.5,
                ease: "easeInOut",
                delay: 2,
              }}
            >
              <div className="flex items-center gap-2">
                <Linkedin className="h-5 w-5" />
                <span className="font-medium">New comment</span>
              </div>
            </motion.div>

            <motion.div
              className="absolute hidden md:block bottom-8 -left-12 bg-primary/80 text-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, 8, 0],
                rotate: [0, 1, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 4,
                ease: "easeInOut",
                delay: 1,
              }}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <span className="font-medium">New ad comment</span>
              </div>
            </motion.div>

            {/* Right side - Actions */}
            <motion.div
              className="absolute hidden md:block top-1/4 -right-8 bg-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, 8, 0],
                rotate: [0, -2, 0]
              }}
              transition={{
                repeat: Infinity,
                duration: 4,
                ease: "easeInOut",
                delay: 1
              }}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-yellow-500" />
                <span className="font-medium">AI Response</span>
              </div>
            </motion.div>

            <motion.div
              className="absolute hidden md:block top-8 -right-10 bg-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, -6, 0],
                rotate: [0, 1, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 4.2,
                ease: "easeInOut",
                delay: 0.7,
              }}
            >
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <span className="font-medium">Quick Reply</span>
              </div>
            </motion.div>

            <motion.div
              className="absolute hidden md:block top-1/2 -right-12 bg-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, 10, 0],
                rotate: [0, -1.5, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 5,
                ease: "easeInOut",
                delay: 1.2,
              }}
            >
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                <span className="font-medium">Hiding comment</span>
              </div>
            </motion.div>

            <motion.div
              className="absolute hidden md:block bottom-12 -right-10 bg-white p-3 rounded-lg shadow-lg"
              animate={{
                y: [0, -8, 0],
                rotate: [0, 2, 0],
              }}
              transition={{
                repeat: Number.POSITIVE_INFINITY,
                duration: 4.8,
                ease: "easeInOut",
                delay: 1.8,
              }}
            >
              <div className="flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                <span className="font-medium">Deleting comment</span>
              </div>
            </motion.div>


          </motion.div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="w-full h-auto">
            <path
              fill="currentColor"
              fillOpacity="0.1"
              d="M0,64L60,69.3C120,75,240,85,360,80C480,75,600,53,720,48C840,43,960,53,1080,58.7C1200,64,1320,64,1380,64L1440,64L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z"
            ></path>
          </svg>
        </div>
      </motion.section>

      {/* Features Section */}
      <section ref={featuresRef} className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Powerful Features to Streamline Your Workflow</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Ripli combines AI technology with intuitive design to help you manage social media comments efficiently.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 * index }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="mb-4">{feature.icon}</div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
              <Link href="/auth/signin" className="flex items-center gap-2"> {/* Use next/link component */}
                <Shield className="h-5 w-5 text-primary" />
                <span className="font-medium">{TRIAL_DAYS}-day free trial</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section ref={howItWorksRef} className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={howItWorksInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">How Ripli Works</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Get started in minutes and transform your social media management
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: "1",
                title: "Connect Your Accounts",
                description: "Link your Instagram, Facebook, YouTube, and LinkedIn accounts with just a few clicks.",
                delay: 0.2
              },
              {
                step: "2",
                title: "Manage Comments",
                description: "View and respond to all comments across platforms from a single dashboard.",
                delay: 0.4
              },
              {
                step: "3",
                title: "Use AI Responses",
                description: "Generate intelligent responses with AI, review, and send with one click.",
                delay: 0.6
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={howItWorksInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: item.delay }}
                className="relative"
              >
                <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                  <span className="text-primary font-bold text-lg">{item.step}</span>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-6 left-[60%] right-0 h-0.5 bg-primary/20"></div>
                )}
                <h3 className="text-xl font-semibold mb-2 text-center">{item.title}</h3>
                <p className="text-muted-foreground text-center">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms Section */}
      <section ref={platformsRef} className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={platformsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">All Your Platforms in One Place</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Ripli seamlessly integrates with all major social media platforms
            </p>
          </motion.div>

          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {[
              { icon: <Instagram className="h-12 w-12 md:h-16 md:w-16" />, name: "Instagram" },
              { icon: <Facebook className="h-12 w-12 md:h-16 md:w-16" />, name: "Facebook" },
              { icon: <Youtube className="h-12 w-12 md:h-16 md:w-16" />, name: "YouTube" },
              { icon: <Linkedin className="h-12 w-12 md:h-16 md:w-16" />, name: "LinkedIn" }
            ].map((platform, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={platformsInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5, delay: 0.2 * index }}
                className="text-center"
              >
                <div className="text-primary/80 mb-2">{platform.icon}</div>
                <span className="font-medium">{platform.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section ref={pricingRef} className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={pricingInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that works best for your needs
            </p>
          </motion.div>

          <Tabs defaultValue="monthly" className="max-w-4xl mx-auto">
            <TabsList className="grid w-[400px] max-w-full grid-cols-2 mx-auto mb-8">
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="yearly">Yearly (Save 20%)</TabsTrigger>
            </TabsList>

            <TabsContent value="monthly">
              <div className="grid md:grid-cols-2 gap-8">
                {Object.values(PLANS).map((plan, index) => {
                  const isPopular = plan.name === PLANS.COMPLETE_PACKAGE.name;
                  const delay = index * 0.2 + 0.2; // Stagger animation slightly
                  return (
                    <motion.div
                      key={plan.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={pricingInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ duration: 0.5, delay: delay }}
                    >
                      <Card className={`h-full overflow-hidden ${isPopular ? 'border-primary shadow-lg' : ''}`}>
                        {isPopular && (
                          <div className="bg-primary text-primary-foreground text-center py-1 text-sm font-medium">
                            Most Popular
                          </div>
                        )}
                        <CardContent className={`p-6 ${isPopular ? 'pt-5' : 'pt-6'}`}>
                          <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                          <div className="mb-4">
                            <span className="text-4xl font-bold">${plan.monthlyPrice.toFixed(2)}</span>
                            <span className="text-muted-foreground">/month</span>
                          </div>
                          <p className="text-muted-foreground mb-6">{plan.description}</p>
                          <Button className="w-full mb-6" variant={isPopular ? "default" : "outline"} asChild>
                            <Link href="/auth/signin">Start Free Trial</Link>
                          </Button>
                          <ul className="space-y-3">
                            {plan.features.map((feature, i) => (
                              <li key={i} className="flex items-start">
                                <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="yearly">
              <div className="grid md:grid-cols-2 gap-8">
                {Object.values(PLANS).map((plan, index) => {
                  const isPopular = plan.name === PLANS.COMPLETE_PACKAGE.name;
                  const yearlyMonthlyPrice = (plan.yearlyPrice / 12).toFixed(2);
                  const delay = index * 0.2 + 0.2; // Stagger animation slightly
                  return (
                    <motion.div
                      key={plan.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={pricingInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ duration: 0.5, delay: delay }}
                    >
                      <Card className={`h-full overflow-hidden ${isPopular ? 'border-primary shadow-lg' : ''}`}>
                        {isPopular && (
                          <div className="bg-primary text-primary-foreground text-center py-1 text-sm font-medium">
                            Most Popular
                          </div>
                        )}
                        <CardContent className={`p-6 ${isPopular ? 'pt-5' : 'pt-6'}`}>
                          <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                          <div className="mb-4">
                            <span className="text-4xl font-bold">${yearlyMonthlyPrice}</span>
                            <span className="text-muted-foreground">/month</span>
                            <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Save 20%</span>
                          </div>
                          <p className="text-muted-foreground mb-6">{plan.description}</p>
                          <Button className="w-full mb-6" variant={isPopular ? "default" : "outline"} asChild>
                            <Link href="/auth/signin">Start Free Trial</Link>
                          </Button>
                          <ul className="space-y-3">
                            {plan.features.map((feature, i) => (
                              <li key={i} className="flex items-start">
                                <CheckCircle className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Testimonials Section */}
      <section ref={testimonialsRef} className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Our Customers Say</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Join hundreds of satisfied users who have transformed their social media management
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 * index }}
              >
                <Card className="h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center mb-4">
                      <Image
                        src={testimonial.avatar || "/placeholder.svg"}
                        alt={testimonial.name}
                        width={48} // w-12 -> 3rem -> 48px
                        height={48} // h-12 -> 3rem -> 48px
                        className="rounded-full mr-4"
                      />
                      <div>
                        <h4 className="font-semibold">{testimonial.name}</h4>
                        <p className="text-sm text-muted-foreground">{testimonial.role}, {testimonial.company}</p>
                      </div>
                    </div>
                    <p className="italic">&quot;{testimonial.content}&quot;</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: "How does the 7-day free trial work?",
                answer: "You can sign up for Ripli and use all features for 7 days without any charge. If you dicide Ripli isn't for you, cancel within the trial and you won't start your subscription."
              },
              {
                question: "Can I connect multiple accounts from the same platform?",
                answer: "Yes, you can connect multiple Instagram, Facebook, YouTube, and LinkedIn accounts to Ripli, depending on your plan. This is perfect for agencies or businesses managing multiple brands."
              },
              {
                question: "How accurate are the AI-generated responses?",
                answer: "Our AI is trained on millions of social media interactions, understands context and you can set a tone. You can also review and edit all AI-generated responses before sending them to ensure they match your communication style."
              },
              {
                question: "Is there a limit to how many comments I can manage?",
                answer: "No, all plans include unlimited comment management. You can respond to as many comments as you need across all your connected platforms. AI replies are limited to 1000 or 2000 per month depending on your plan."
              },
              {
                question: "Can I cancel my subscription at any time?",
                answer: "Yes, you can cancel your subscription at any time. If you cancel, you'll still have access to Ripli until the end of your current billing period."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
              >
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={ctaInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Social Media Management?</h2>
            <p className="text-xl mb-8 text-muted-foreground">
              Join to save time and boost engagement with Ripli.
            </p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={ctaInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button size="lg" className="text-lg px-8" asChild>
                <Link href="/auth/signin"><ArrowRight className="ml-2 h-5 w-5" /> Start Free Trial</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
